import React from 'react';
import Modal from '../../../components/base/Modal';
import ViewModeToggle from '../../../components/common/ViewModeToggle';
import InputLabel from '../../../components/base/InputLabel';
import NumberInput from '../../../components/base/NumberInput';
import MultiTpSettings from '../../../components/metrics/MultiTpSettings';
import BigNumber from 'bignumber.js';
import { ERROR_MESSAGES } from '../../../errors/constants';
import { MIN_VALUES } from '../../../constants/minValues';

const MicroLabel = ({ children }) => (
  <div className="text-[10px] text-white/30 mb-0.5 uppercase tracking-wider">{children}</div>
);

const ForexSettingsModal = ({ isOpen, onClose, values, setValues, errors = {}, initialFocusField, metrics = {}, viewMode, onViewModeChange }) => {
  const feeBreakdown = metrics.feeBreakdown || {};

  const formatFeeResult = (val) => {
    if (!val || val === '--') return '--';
    return val;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Settings"
      wide
    >
      <div className="space-y-6 pb-10">
        {/* View mode — Full / Compact (moved here from the metrics card) */}
        <div className="flex items-center justify-between">
          <InputLabel>View</InputLabel>
          <ViewModeToggle viewMode={viewMode} onChange={onViewModeChange} />
        </div>

        {/* Risk and Account Balance */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="flex justify-between mb-2">
              <InputLabel required>Risk</InputLabel>
              <div className="flex gap-1">
                <button
                  onClick={() => setValues(prev => ({ ...prev, riskUnit: 'percent' }))}
                  className={`px-2.5 py-1 rounded-full text-xs transition-colors ${values.riskUnit === 'percent'
                    ? 'bg-primary-orange/20 text-primary-orange'
                    : 'hover:bg-slate-800 text-slate-500'
                    }`}
                >
                  %
                </button>
                <button
                  onClick={() => setValues(prev => ({ ...prev, riskUnit: 'fixed' }))}
                  className={`px-2.5 py-1 rounded-full text-xs transition-colors ${values.riskUnit === 'fixed'
                    ? 'bg-primary-orange/20 text-primary-orange'
                    : 'hover:bg-slate-800 text-slate-500'
                    }`}
                >
                  $
                </button>
              </div>
            </div>
            <NumberInput
              value={values.risk}
              onChange={(value) => setValues(prev => ({
                ...prev,
                risk: value || new BigNumber(0)
              }))}
              suffix={values.riskUnit === 'percent' ? '%' : undefined}
              prefix={values.riskUnit === 'fixed' ? '$' : undefined}
              decimals={2}
              required
              min={MIN_VALUES.risk}
              error={errors.risk ? { message: ERROR_MESSAGES.INVALID_RISK } : null}
            />
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <InputLabel required>Account Balance</InputLabel>
              <div className="h-[28px]"></div>
            </div>
            <NumberInput
              value={values.accountBalance}
              onChange={(value) => setValues(prev => ({
                ...prev,
                accountBalance: value || new BigNumber(0)
              }))}
              prefix="$"
              decimals={2}
              required
              min={MIN_VALUES.accountBalance}
              error={errors.accountBalance ? { message: ERROR_MESSAGES.INVALID_ACCOUNT_BALANCE } : null}
              autoFocus={initialFocusField === 'accountBalance'}
            />
          </div>
        </div>

        {/* R:R and Leverage */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <InputLabel required>Nominal R:R</InputLabel>
            <NumberInput
              value={values.nominalRR}
              onChange={(value) => setValues(prev => ({
                ...prev,
                nominalRR: value || new BigNumber(0)
              }))}
              suffix="x"
              decimals={1}
              required
              min={MIN_VALUES.nominalRR}
              error={errors.nominalRR ? { message: ERROR_MESSAGES.INVALID_RR } : null}
            />
          </div>

          <div>
            <InputLabel required>Max Leverage</InputLabel>
            <NumberInput
              value={values.maxLeverage}
              onChange={(value) => setValues(prev => ({
                ...prev,
                maxLeverage: value || new BigNumber(0)
              }))}
              suffix="x"
              decimals={0}
              required
              min={MIN_VALUES.maxLeverage}
              error={errors.maxLeverage ? { message: ERROR_MESSAGES.INVALID_LEVERAGE } : null}
            />
          </div>
        </div>

        {/* Safety buffer (risk multiple) */}
        <div>
          <InputLabel required>Safety Buffer</InputLabel>
          <NumberInput
            value={values.safetyBufferR}
            onChange={(value) => setValues(prev => ({
              ...prev,
              safetyBufferR: value || MIN_VALUES.safetyBufferR
            }))}
            suffix="R"
            decimals={2}
            step={0.25}
            required
            min={MIN_VALUES.safetyBufferR}
            error={errors.safetyBufferR ? { message: ERROR_MESSAGES.INVALID_MARGIN } : null}
          />
          <p className="text-xs text-slate-500 mt-1.5">
            Keeps liquidation this many stop-distances beyond your stop — 2R = two stops past it.
          </p>
        </div>

        {/* Divider */}
        <div className="border-t border-white/[0.06]" />

        {/* FEE BREAKDOWN */}
        <div>
          <div className="text-slate-500 uppercase tracking-[2px] text-[10px] font-bold mb-3">Fee Breakdown</div>

          {/* Fee input grid */}
          <div className="bg-white/[0.025] rounded-[10px] overflow-hidden mb-3">

            {/* Spread */}
            <div className="grid grid-cols-[1fr_140px] items-center gap-x-3 px-3.5 py-2.5 border-b border-white/5">
              <span className="text-white/60 text-[12px]">Spread</span>
              <div>
                <MicroLabel>Pips</MicroLabel>
                <NumberInput
                  value={values.spread}
                  onChange={(value) => setValues(prev => ({ ...prev, spread: value || new BigNumber(0) }))}
                  decimals={1}
                  showSpinners={false}
                  autoFocus={initialFocusField === 'fees'}
                />
              </div>
            </div>

            {/* Commission */}
            <div className="grid grid-cols-[1fr_140px] items-center gap-x-3 px-3.5 py-2.5 border-b border-white/5">
              <div className="flex flex-col gap-1.5">
                <span className="text-white/60 text-[12px]">Commission</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setValues(prev => ({ ...prev, commType: 'percent' }))}
                    className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${values.commType === 'percent'
                      ? 'bg-primary-orange/30 text-amber-500'
                      : 'bg-white/[0.08] text-slate-500 hover:text-slate-400'
                      }`}
                  >
                    % position
                  </button>
                  <button
                    onClick={() => setValues(prev => ({ ...prev, commType: 'per_lot' }))}
                    className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${values.commType === 'per_lot'
                      ? 'bg-primary-orange/30 text-amber-500'
                      : 'bg-white/[0.08] text-slate-500 hover:text-slate-400'
                      }`}
                  >
                    $/lot
                  </button>
                </div>
              </div>
              <div>
                <MicroLabel>{values.commType === 'percent' ? 'Rate %' : '$/lot RT'}</MicroLabel>
                <NumberInput
                  value={values.commRate}
                  onChange={(value) => setValues(prev => ({ ...prev, commRate: value || new BigNumber(0) }))}
                  suffix={values.commType === 'percent' ? '%' : undefined}
                  prefix={values.commType === 'per_lot' ? '$' : undefined}
                  decimals={values.commType === 'percent' ? 5 : 2}
                  showSpinners={false}
                />
              </div>
            </div>

            {/* Swap Long */}
            <div className="grid grid-cols-[1fr_140px] items-center gap-x-3 px-3.5 py-2.5 border-b border-white/5">
              <span className="text-white/60 text-[12px]">Swap Long</span>
              <div>
                <MicroLabel>$/lot</MicroLabel>
                <NumberInput
                  value={values.swapLong}
                  onChange={(value) => setValues(prev => ({ ...prev, swapLong: value || new BigNumber(0) }))}
                  prefix="$"
                  decimals={2}
                  showSpinners={false}
                  allowNegative={true}
                />
              </div>
            </div>

            {/* Swap Short */}
            <div className="grid grid-cols-[1fr_140px] items-center gap-x-3 px-3.5 py-2.5 border-b border-white/5">
              <span className="text-white/60 text-[12px]">Swap Short</span>
              <div>
                <MicroLabel>$/lot</MicroLabel>
                <NumberInput
                  value={values.swapShort}
                  onChange={(value) => setValues(prev => ({ ...prev, swapShort: value || new BigNumber(0) }))}
                  prefix="$"
                  decimals={2}
                  showSpinners={false}
                  allowNegative={true}
                />
              </div>
            </div>

            {/* Nights */}
            <div className="grid grid-cols-[1fr_140px] items-center gap-x-3 px-3.5 py-2.5">
              <span className="text-white/60 text-[12px]">Nights</span>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <MicroLabel>Nights</MicroLabel>
                  <NumberInput
                    value={values.nights}
                    onChange={(value) => setValues(prev => ({ ...prev, nights: value || new BigNumber(0) }))}
                    decimals={0}
                    showSpinners={false}
                  />
                </div>
                <button
                  onClick={() => setValues(prev => ({ ...prev, wedEnabled: !prev.wedEnabled }))}
                  className={`px-2 py-1.5 rounded text-[10px] font-semibold transition-colors whitespace-nowrap mb-[1px] ${values.wedEnabled
                    ? 'bg-primary-orange/30 text-amber-500'
                    : 'bg-white/[0.08] text-slate-500 hover:text-slate-400'
                    }`}
                >
                  +Wed ×3
                </button>
              </div>
            </div>

          </div>

          {/* Fee Results (live calculated) */}
          <div className="bg-white/[0.025] rounded-[10px] overflow-hidden mt-3">
            <div className="flex justify-between items-center px-3.5 py-2 border-b border-white/5">
              <span className="text-slate-500 text-[12px]">Spread Cost</span>
              <span className="text-slate-300/80 text-[13px] font-medium min-w-[64px] text-right font-mono">{formatFeeResult(feeBreakdown.spreadCost)}</span>
            </div>
            <div className="flex justify-between items-center px-3.5 py-2 border-b border-white/5">
              <span className="text-slate-500 text-[12px]">Entry Commission</span>
              <span className="text-slate-300/80 text-[13px] font-medium min-w-[64px] text-right font-mono">{formatFeeResult(feeBreakdown.entryComm)}</span>
            </div>
            <div className="flex justify-between items-center px-3.5 py-2 border-b border-white/5">
              <span className="text-slate-500 text-[12px]">Exit Commission</span>
              <span className="text-slate-300/80 text-[13px] font-medium min-w-[64px] text-right font-mono">{formatFeeResult(feeBreakdown.exitComm)}</span>
            </div>
            <div className="flex justify-between items-center px-3.5 py-2 border-b border-white/[0.08]">
              <span className="text-slate-500 text-[12px]">Swap</span>
              <span className={`text-[13px] font-medium min-w-[64px] text-right font-mono ${
                feeBreakdown.swapIsNegative ? 'text-orange-400' : feeBreakdown.swapIsIncome ? 'text-emerald-400' : 'text-slate-300/80'
              }`}>{formatFeeResult(feeBreakdown.swapCost)}</span>
            </div>
            <div className="flex justify-between items-center px-3.5 py-2.5">
              <span className="text-white font-bold text-[13px]">Total Round Turn</span>
              <span className="text-amber-500 text-[15px] font-extrabold min-w-[64px] text-right font-mono">{formatFeeResult(feeBreakdown.totalRoundTurn)}</span>
            </div>
          </div>

          <p className="text-white/[0.18] text-[11px] leading-relaxed mt-3">
            Spread — entry only. Commission — both sides. Swap — per night held.
            {values.nights && values.nights.gt && values.nights.gt(0) && " Wednesday = triple swap (weekend rollover)."}
          </p>
        </div>

        <MultiTpSettings values={values} onChange={(p) => setValues((prev) => ({ ...prev, ...p }))} />
      </div>
    </Modal>
  );
};

export default ForexSettingsModal;
